import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const FavouritesContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FavouritesProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [favouriteIds, setFavouriteIds] = useState(new Set());

  const fetchFavourites = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/favourites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFavouriteIds(new Set(data.map(p => p.id)));
      }
    } catch (e) {
      console.error('Error fetching favourites:', e);
    }
  }, [token]);

  useEffect(() => {
    if (token && user) {
      fetchFavourites();
    } else {
      setFavouriteIds(new Set());
    }
  }, [token, user, fetchFavourites]);

  const isFavourite = useCallback((productId) => favouriteIds.has(productId), [favouriteIds]);

  const toggleFavourite = useCallback(async (productId) => {
    if (!token) {
      toast.error('Please login to save favourites');
      return;
    }
    const currentlyFav = favouriteIds.has(productId);
    try {
      if (currentlyFav) {
        await fetch(`${API}/favourites/${productId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavouriteIds(prev => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
        toast.success('Removed from saved items');
      } else {
        await fetch(`${API}/favourites/${productId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavouriteIds(prev => new Set([...prev, productId]));
        toast.success('Saved for later');
      }
    } catch (e) {
      toast.error('Failed to update saved items');
    }
  }, [token, favouriteIds]);

  const count = favouriteIds.size;

  return (
    <FavouritesContext.Provider value={{
      favouriteIds, count, isFavourite, toggleFavourite, fetchFavourites
    }}>
      {children}
    </FavouritesContext.Provider>
  );
};

export const useFavourites = () => {
  const context = useContext(FavouritesContext);
  if (!context) {
    throw new Error('useFavourites must be used within FavouritesProvider');
  }
  return context;
};
